/* eslint-disable no-secrets/no-secrets */
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { wizardProps, WizardContext } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { DocumentService } from 'wmic-pe-capability-gateway-policycommon';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDocumentTitle, useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { WMICUnderwritingIssues } from 'wmic-pe-components-platform-react';
import { WMICLogger, CONSTANTS, WMICVariousUtil, parseErrorMessage } from 'wmic-pe-portals-utils-js';
import { WMICErrorHandler, WMICQuoteUtil } from 'wmic-pe-capability-quoteandbind-common-react';
import _ from 'lodash';
import { WMICDocuments, FlexUtil } from 'wmic-pe-capability-gateway-common-react';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';

import metadata from './WMICPAQuotePage.metadata.json5';
import messages from './WMICPAQuotePage.messages';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICPAQuotePage(props) {
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const { wizardData: submissionVM, updateWizardData, markStepSubmitted, stopErrorNavigation } = props;
    const {
        isComponentValid,
        onValidate,
        initialValidation,
        registerComponentValidation,
        registerInitialComponentValidation,
    } = useValidation('QuotePage');
    const history = useHistory();
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { isSkipping } = useContext(WizardContext);
    const { setWizardLoading } = useWizardModals();
    const translator = useContext(TranslatorContext);
    const [userDocuments, setUserDocuments] = useState([]);

    useDocumentTitle(translator(messages.quotePageQuoteSummaryCardTitle), submissionVM);

    const fetchDocuments = useCallback(async () => {
        const documents = await DocumentService.getDocumentsForJobNumber(submissionVM.quoteID.value, authHeader);
        if (userDocuments.length !== documents.length) {
            setUserDocuments(documents);
        }
    }, [authHeader, submissionVM, userDocuments]);

    const validateSubmission = useCallback(async () => {
        const result = await LoadSaveService.validateSubmissionForIssue(submissionVM.value, authHeader);
        _.extend(submissionVM.value, result);
        updateWizardData(submissionVM);
        markStepSubmitted(); // the step needs to be marked as submitted for the errors associated with this step to show up
        // Omitting updateWizardData from the dependency array because it causes an infinite loop when called from the useEffect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [LoadSaveService, submissionVM, authHeader, viewModelService, markStepSubmitted]);

    const underwritingIssues = _.get(submissionVM, 'errorsAndWarnings.underwritingIssues.value', []);

    const hasQuoteBlockingUWIssues = useCallback(
        () => WMICQuoteUtil.hasQuoteBlockingUWIssues(submissionVM),
        [submissionVM]
    );

    const isTransactionQuoted = useCallback(
        () => submissionVM?.baseData?.periodStatus?.value?.code === CONSTANTS.QUOTED,
        [submissionVM]
    );

    const checkComponentValidation = useCallback(
        () =>
            !(
                WMICQuoteUtil.isBlockingUWIssuePresent(submissionVM) ||
                WMICQuoteUtil.hasValidationErrors(submissionVM) ||
                hasQuoteBlockingUWIssues() ||
                !isTransactionQuoted() ||
                FlexUtil.hasInvalidFlexFields(submissionVM)
            ),
        [submissionVM, hasQuoteBlockingUWIssues, isTransactionQuoted]
    );

    const checkInitialValidation = useCallback(async () => {
        if (isSkipping) {
            let isValidateSubmissionSuccessful = false;
            await validateSubmission()
                .then(() => {
                    isValidateSubmissionSuccessful = true;
                })
                .catch(() => {
                    isValidateSubmissionSuccessful = false;
                });
            return isValidateSubmissionSuccessful && checkComponentValidation();
        }
        return true;
    }, [isSkipping, validateSubmission, checkComponentValidation]);

    useEffect(() => {
        registerInitialComponentValidation(checkInitialValidation);
        registerComponentValidation(checkComponentValidation);
        stopErrorNavigation();
    }, [
        registerInitialComponentValidation,
        checkInitialValidation,
        registerComponentValidation,
        checkComponentValidation,
        stopErrorNavigation,
    ]);

    const loadAdditionalInformation = useCallback(async () => {
        const isBMSi = _.get(submissionVM, 'isFromBMS_WMIC.value');
        const defaultedPropertiesToReviewExist = _.get(submissionVM, 'defaultedProperties_WMIC.value', []).some(
            p => p.propertyDisplayName && p.status !== CONSTANTS.REVIEWED
        );
        // All default values should be reviewed to avoid
        // causing warning messages to disappear unintentionally.
        const shouldRunValidateSubmission = !isSkipping && (!isBMSi || (isBMSi && !defaultedPropertiesToReviewExist));

        setWizardLoading(!isSkipping, translator(commonMessages.loading));
        await Promise.all([shouldRunValidateSubmission ? validateSubmission() : null, fetchDocuments()])
            .catch(error => {
                const jobID = submissionVM.quoteID.value;
                WMICLogger.error('Unable to load additional information', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() =>
                    history.push(`/quotes/${jobID}/summary`)
                );
            })
            .finally(() => setWizardLoading(false));
    }, [isSkipping, translator, submissionVM, validateSubmission, fetchDocuments, setWizardLoading, history]);

    useEffect(() => {
        loadAdditionalInformation();
    }, [loadAdditionalInformation]);

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            WMICVariousUtil.scrollToTop();
            return false;
        }
        return submissionVM;
    }, [isComponentValid, submissionVM]);

    const isQuoteInfoAvailable = !hasQuoteBlockingUWIssues() && isTransactionQuoted();
    const isUploadDocumentsSectionVisible = !hasQuoteBlockingUWIssues() && isTransactionQuoted();

    const overrideProps = {
        wmicFlexAlertMessage: {
            jobVM: submissionVM,
        },
        wmicFlexValidationErrorMessage: {
            jobVM: submissionVM,
        },
        wmicQuoteAlertMessage: {
            jobVM: submissionVM,
        },
        quotePagePremiumCosts: {
            premium: _.get(submissionVM, 'quoteData.offeredQuotes.value[0].premium', {}),
            jobVM: submissionVM,
            enableFlex: true,
            enableBrokerFees: true,
            updateJobVM: updateWizardData,
            visible: isQuoteInfoAvailable,
        },
        quotePageUWIssues: {
            underwritingIssues,
            quoteID: _.get(submissionVM, 'quoteID.value'),
            authHeader,
            jobVM: submissionVM,
        },
        quotePageSummary: {
            jobVM: submissionVM,
            lobData: _.get(submissionVM, 'lobData.personalAuto', {}),
            updateWizardData,
            visible: isQuoteInfoAvailable,
        },
        quotePageUploadDocuments: {
            documents: userDocuments,
            updateDocuments: setUserDocuments,
            value: {
                documentDTOs: userDocuments,
                canUploadDocument: true,
                jobNumber: submissionVM.quoteID.value,
            },
            visible: isUploadDocumentsSectionVisible,
        },
    };
    const resolvers = {
        componentMap: {
            WMICUnderwritingIssues,
            WMICDocuments,
        },
    };

    return (
        <WMICWizardSubmissionPage
            onNext={onNext}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            cancelLabel={translator(commonMessages.saveAndExit)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                componentMap={resolvers.componentMap}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPAQuotePage.propTypes = wizardProps;

export default WMICPAQuotePage;
